import { Injectable, inject } from "@angular/core";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";

import {
    BehaviorSubject,
    Observable,
    Subject,
    catchError,
    distinctUntilChanged,
    throwError
} from "rxjs";

import { AppStorageService } from "./app-storage.service";

type EtagData<T> = T & { etag: string };
export type HttpResponseWithEtag<T> = HttpResponse<EtagData<T>>;
interface PendingRequest<T> {
    responseSubject: Subject<HttpResponse<EtagData<T>>>;
    requestInvoker: (etag: string) => Observable<HttpResponse<EtagData<T>>>;
}

@Injectable({ providedIn: "root" })
export class AppEtagManagerService {
    private _appStorageService = inject(AppStorageService);
    private _pendingRequests: Array<PendingRequest<any>> = [];

    private _etag: BehaviorSubject<string> = new BehaviorSubject<string>(
        this._appStorageService.getFromSessionStorage("etag") || ""
    );

    private _etagRenewed$ = new Subject<void>();

    public etag$ = this._etag.asObservable().pipe(distinctUntilChanged());
    /** Emits on every etag set (including same value), useful for activity tracking. */
    public etagRenewed$ = this._etagRenewed$.asObservable();

    setEtag(etag: string): void {
        this._etag.next(etag);

        if (etag) {
            this._appStorageService.setToSessionStorage("etag", etag);
            this._etagRenewed$.next();
        } else {
            this._appStorageService.removeFromSessionStorage("etag");
        }
    }

    clear(clearEtag = true): void {
        this._pendingRequests = [];
        if (clearEtag) {
            this.setEtag("");
        }
    }

    invokeRequestWithEtagRefresh$<T>(
        requestInvoker: (etag: string) => Observable<HttpResponseWithEtag<T>>
    ): Observable<HttpResponseWithEtag<T>> {
        const responseSubject = new Subject<HttpResponseWithEtag<T>>();

        this._pendingRequests.push({
            requestInvoker,
            responseSubject
        });

        if (this._pendingRequests.length === 1) {
            this._invokeNextPendingRequest(this._etag.value);
        }

        return responseSubject.asObservable();
    }

    private _invokeNextPendingRequest(etag: string): void {
        const pendingRequest = this._pendingRequests[0];

        if (!pendingRequest) {
            return;
        }

        pendingRequest
            .requestInvoker(etag)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    this._pendingRequests.forEach((pending, index) => {
                        if (index === 0) {
                            pending.responseSubject.error(error);
                        }

                        pending.responseSubject.complete();
                    });

                    this.clear(false);

                    return throwError(() => error);
                })
            )
            .subscribe(response => {
                if (!this._pendingRequests[0]) {
                    return;
                }

                const shiftedRequest = this._pendingRequests.shift();

                if (shiftedRequest) {
                    shiftedRequest.responseSubject.next(response);
                    shiftedRequest.responseSubject.complete();
                }

                if (!response.body || !response.body.etag) {
                    this._appStorageService.removeFromSessionStorage("lockedSubjectName");
                    this.clear();
                    return;
                }

                if (this._pendingRequests.length > 0) {
                    this._invokeNextPendingRequest(response.body.etag);
                    return;
                }

                this.setEtag(response.body.etag);
            });
    }
}
